<template>
  <div
    :class="[
      $style.container,
      {
        [$style.is24]: isIs24,
        [$style.hg]: !isIs24,
      },
    ]"
  >
    <div :class="$style.imageWrapper">
      <img
        :class="$style.rotatingWidget"
        :src="currentWidgetUrl"
        loading="lazy"
        :alt="$t(`landing.homeownerworld.alt.feature${currentWidgetIndex + 1}`)"
      />
      <img
        v-if="overlayWidgetUrl"
        :class="[$style.rotatingWidget, $style.overlayWidget]"
        :src="overlayWidgetUrl"
        loading="lazy"
        :alt="$t(`landing.homeownerworld.alt.feature${currentWidgetIndex + 1}`)"
      />
    </div>
    <div :class="$style.contentWrapper">
      <HgBadge :theme="'info'" :class="$style.badge" style="color: #333333">
        {{ $t('landing.homeownerworld.badge') }}
      </HgBadge>
      <h2 :class="$style.heading">{{ $t('landing.homeownerworld.title') }}</h2>
      <p :class="$style.description">
        {{ $t('landing.homeownerworld.body') }}
      </p>

      <div :class="$style.valueProposition">
        <div :class="isIs24 ? $style.valueBoxIs24 : $style.valueBoxHg">
          <div :class="$style.valuePropositionItem">
            <HgIcon
              :class="$style.valuePropositionIcon"
              :icon="isIs24 ? 'BarChart' : 'Growth'"
              :size="isIs24 ? '2x-large' : 'large'"
              :theme="'secondary'"
            />
            <p :class="$style.valuationPropositionDescription">
              {{ $t('landing.homeownerworld.prompt.track') }}
            </p>
          </div>
        </div>
        <div :class="isIs24 ? $style.valueBoxIs24 : $style.valueBoxHg">
          <div :class="$style.valuePropositionItem">
            <HgIcon
              :class="$style.valuePropositionIcon"
              icon="PriceTag"
              :size="isIs24 ? 'x-large' : 'large'"
              theme="secondary"
            />
            <p :class="$style.valuationPropositionDescription">
              {{ $t('landing.homeownerworld.prompt.price') }}
            </p>
          </div>
        </div>
        <div :class="isIs24 ? $style.valueBoxIs24 : $style.valueBoxHg">
          <div :class="$style.valuePropositionItem">
            <HgIcon
              :class="$style.valuePropositionIcon"
              icon="Map"
              :size="isIs24 ? 'x-large' : 'large'"
              theme="secondary"
              variant="default"
            />
            <p :class="$style.valuationPropositionDescription">
              {{ $t('landing.homeownerworld.prompt.explore') }}
            </p>
          </div>
        </div>
      </div>
      <div
        :class="[
          $style.ctaButtonWrapper,
          {
            [$style.is24]: isIs24,
            [$style.hg]: !isIs24,
          },
        ]"
      >
        <HgButton :class="$style.ctaButton" :theme="'primary'" @click="onClickCtaButton">
          {{ $t('landing.myProperties.cta') }}
        </HgButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { HgBadge, HgButton, HgIcon } from '@smg-real-estate/domus';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { loadHomeOwnerWidgetImageUrls } from '@/components/landing-page/HomeOwnerWorldSection/homeOwnerWidgetImages';
import { isPlatformIs24 } from '@/helpers/Platform/Platform';
import {
  OVERVIEW_PAGE_PATH,
  SOURCE_QUERY_STRING_PARAMETER,
  SOURCE_QUERY_STRING_VALUES,
} from '@/router/routes/constants';
import { trackingClickEvent } from '@/tracking/tracking';
import { TRACKING_ACTION, TRACKING_LABEL } from '@/tracking/types';

const { t, locale } = useI18n();

const isIs24 = isPlatformIs24();
const currentWidgetIndex = ref(0);
const widgetCache = ref<Map<string, string[]>>(new Map());
const rotationInterval = ref<number | null>(null);
const overlayWidgetUrl = ref<string>('');
const overlayTimeout = ref<number | null>(null);

const currentWidgetUrl = computed(() => {
  const localeWidgets = widgetCache.value.get(locale.value);
  if (!localeWidgets || localeWidgets.length === 0) {
    return '';
  }
  return localeWidgets[currentWidgetIndex.value];
});

const onClickCtaButton = () => {
  trackingClickEvent(TRACKING_ACTION.GO_TO_MY_REAL_ESTATE, TRACKING_LABEL.VALUATION_FUNNEL);

  gotToRealEstateHub();
};

const gotToRealEstateHub = () => {
  window.location.href = `${OVERVIEW_PAGE_PATH[locale.value]}?${SOURCE_QUERY_STRING_PARAMETER}=${SOURCE_QUERY_STRING_VALUES.VALUATION_FUNNEL_LANDING}`;
};

const loadWidgetImagesForLocale = async (targetLocale: string): Promise<string[]> => {
  if (widgetCache.value.has(targetLocale)) {
    return widgetCache.value.get(targetLocale)!;
  }

  const widgetUrls = await loadHomeOwnerWidgetImageUrls(targetLocale);
  widgetCache.value.set(targetLocale, widgetUrls);
  return widgetUrls;
};

const transitionToNextWidget = () => {
  const localeWidgets = widgetCache.value.get(locale.value);
  if (!localeWidgets || localeWidgets.length === 0) {
    return;
  }

  overlayWidgetUrl.value = currentWidgetUrl.value;

  currentWidgetIndex.value = (currentWidgetIndex.value + 1) % localeWidgets.length;

  if (overlayTimeout.value !== null) {
    clearTimeout(overlayTimeout.value);
  }
  overlayTimeout.value = setTimeout(() => {
    overlayWidgetUrl.value = '';
  }, 300) as unknown as number;
};

const startRotation = () => {
  if (rotationInterval.value !== null) {
    clearInterval(rotationInterval.value);
  }

  rotationInterval.value = setInterval(() => {
    transitionToNextWidget();
  }, 3000) as unknown as number;
};

const stopRotation = () => {
  if (rotationInterval.value !== null) {
    clearInterval(rotationInterval.value);
    rotationInterval.value = null;
  }
  if (overlayTimeout.value !== null) {
    clearTimeout(overlayTimeout.value);
    overlayTimeout.value = null;
  }
};

watch(locale, async (newLocale) => {
  if (!widgetCache.value.has(newLocale)) {
    await loadWidgetImagesForLocale(newLocale);
  }

  currentWidgetIndex.value = 0;
  overlayWidgetUrl.value = '';
});

onMounted(async () => {
  await loadWidgetImagesForLocale(locale.value || 'en');

  startRotation();
});

onUnmounted(() => {
  stopRotation();
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.container {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: $border-radius-l;

  &.is24 {
    max-width: 100%;
    margin-right: auto;
    margin-left: auto;

    @include grid-media-query(md) {
      max-width: 1000px;
    }
  }

  &.hg {
    max-width: 100%;
    margin-right: auto;
    margin-left: auto;

    @include grid-media-query(md) {
      max-width: 1000px;
    }
  }

  @include grid-media-query(sm) {
    flex-direction: column;
  }

  @include grid-media-query(md) {
    flex-direction: column;
  }
}
.imageWrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 2 / 1;
  flex-shrink: 0;
  min-height: 300px;
  border-radius: 12px;
  overflow: hidden;

  @include background-image;

  background-image: url('@/assets/images/landing-home-owner-world/how-section-optimized.jpg');
  background-position: center;
  background-size: cover;

  @include grid-media-query(sm) {
    min-height: 350px;
  }

  @include grid-media-query(md) {
    min-height: 400px;
  }
}

.contentWrapper {
  flex: 1;
  padding: $space-250 $space-100;
  background-color: inherit;

  @include grid-media-query(sm) {
    padding: $space-250 0;
  }
}

.badge {
  display: inline-flex;
  align-items: center;
  margin-bottom: $space-100;
}

.trendIcon {
  margin-right: $space-50;
}

.heading {
  @include vertical-margin(0, $space-25);
  @include heading-2;
}

.description {
  @include body-1($color-grey-120);
  @include vertical-margin($space-25, 0);
}

.rotatingWidget {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: min(90%, 300%);
  max-height: min(90%, 300%);
  width: 300%;
  height: auto;
  border-radius: $border-radius-l;
  object-fit: contain;

  @include grid-media-query(sm) {
    max-width: min(85%, 300%);
    max-height: min(85%, 300%);
  }

  @include grid-media-query(md) {
    max-width: min(80%, 300%);
    max-height: min(80%, 300%);
  }

  @include grid-media-query(lg) {
    max-width: min(75%, 300%);
    max-height: min(75%, 300%);
  }
}

.overlayWidget {
  opacity: 1;
  animation: fadeOut 0.3s ease-out forwards;
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

.ctaButtonWrapper {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: $space-200;

  &.hg {
    margin-top: $space-400;
  }
}

.ctaButton {
  width: 100%;
  max-width: 300px;
  padding: 1rem !important;

  @include grid-media-query(sm) {
    max-width: 300px;
  }

  @include grid-media-query(md) {
    max-width: 400px;
  }

  @include grid-media-query(lg) {
    max-width: 450px;
  }
}
.valueProposition {
  display: flex;
  flex-direction: column;
  margin-top: $space-200;

  width: 100%;

  @include grid-media-query(sm) {
    flex-direction: row;
  }
}

.container.is24 {
  .valueProposition {
    @include grid-media-query(sm) {
      gap: $space-200;
    }
  }
}

%valueBox {
  background-size: 100% 100%;
  background-position: center;
  background-repeat: no-repeat;
  flex: 1;
  min-height: 200px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  .valuePropositionItem {
    padding: $space-400;
    margin: 0;
    color: $color-grey-120;

    @include body-2;

    @include grid-media-query(sm) {
      padding: $space-250;
    }

    @include grid-media-query(md) {
      padding: $space-300;
    }
  }

  .valuePropositionIcon {
    margin: 0;
  }

  @include grid-media-query(sm) {
    min-height: 240px;
  }
}

.valueBoxHg {
  @extend %valueBox;
  background-image: url('@/assets/images/landing-home-owner-world/value-proposition-item-hg.svg');
}

.valueBoxIs24 {
  @extend %valueBox;
  background-image: url('@/assets/images/landing-home-owner-world/value-proposition-item-is24.svg');
}
.valuationPropositionDescription {
  @include body-2($color-grey-120);
  margin-top: 0;
  margin-bottom: 0;
  max-width: 15rem;
}
</style>
